import React, { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import {
  BarChart3,
  TrendingUp,
  DollarSign,
  Users,
  Calendar,
  Download,
  Filter,
  PieChart,
  FileText,
  PlayCircle,
  CheckCircle,
  Receipt,
} from "lucide-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { reportService } from "../services/reportService";
import { projectService } from "../services/projectService";
import { subcontractorService } from "../services/subcontractorService";
import { vendorService } from "../services/vendorService";
import { designerService } from "../services/designerService";
import ProjectDetailDialog from "../components/projects/ProjectDetailDialog";
import { useToast } from "../hooks/use-toast";
import { formatDatePST } from "../utils/dateUtils";

export default function Reports() {
  const { toast } = useToast();
  // Edit-cost-item dialog state
  const [editingCostItem, setEditingCostItem] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [savingCostItem, setSavingCostItem] = useState(false);
  const [dateRange, setDateRange] = useState("last_30_days");
  const [reportType, setReportType] = useState("overview");
  const [isCustomDateDialogOpen, setIsCustomDateDialogOpen] = useState(false);
  const [customDateFrom, setCustomDateFrom] = useState("");
  const [customDateTo, setCustomDateTo] = useState("");
  const [isEstimatedRevenueDialogOpen, setIsEstimatedRevenueDialogOpen] = useState(false);
  const [isRevenueReceivedDialogOpen, setIsRevenueReceivedDialogOpen] = useState(false);
  
  // New dialog states for project metrics
  const [isProjectsSoldDialogOpen, setIsProjectsSoldDialogOpen] = useState(false);
  const [isProjectsStartedDialogOpen, setIsProjectsStartedDialogOpen] = useState(false);
  const [isProjectsFinishedDialogOpen, setIsProjectsFinishedDialogOpen] = useState(false);

  // Costs & Wages tab state
  const [costsEntityType, setCostsEntityType] = useState("all");
  const [costsEntityId, setCostsEntityId] = useState("");
  const [costsDateFrom, setCostsDateFrom] = useState("");
  const [costsDateTo, setCostsDateTo] = useState("");
  const [costsSelectedProjects, setCostsSelectedProjects] = useState([]); // Array of {id, name}
  const [projectSearchQuery, setProjectSearchQuery] = useState(""); // Search filter for projects
  const [costsSortBy, setCostsSortBy] = useState("date"); // Column to sort by
  const [costsSortDir, setCostsSortDir] = useState("desc"); // 'asc' or 'desc'

  // Project Detail Dialog state (for viewing project details within Reports page)
  const [selectedProjectId, setSelectedProjectId] = useState(null);
  const [isProjectDetailOpen, setIsProjectDetailOpen] = useState(false);
  const queryClient = useQueryClient();

  // Fetch full project details when a project is selected for viewing
  const { data: selectedProjectData, isLoading: loadingProjectDetails } = useQuery({
    queryKey: ['project-detail', selectedProjectId],
    queryFn: async () => {
      const response = await projectService.getById(selectedProjectId);
      return response.data;
    },
    enabled: !!selectedProjectId && isProjectDetailOpen
  });

  // Handle project click to open detail dialog
  const handleProjectClick = (projectId) => {
    setSelectedProjectId(projectId);
    setIsProjectDetailOpen(true);
  };

  // ---- Edit cost line item ----
  const openEditCostItem = (item) => {
    setEditingCostItem(item);
    const vendorValue = item.vendor_id ? `${item.entity_type || 'vendor'}:${item.vendor_id}` : "";
    setEditForm({
      item_name: item.item_name || "",
      cost: item.cost ?? "",
      vendorValue,
      invoice_number: item.invoice_number || "",
      date: item.date ? String(item.date).slice(0, 10) : "",
      quantity: item.quantity ?? "",
      description: item.description || "",
      target_project_id: item.project_id,
    });
  };

  const handleSaveCostItem = async () => {
    if (!editingCostItem || !editForm) return;
    setSavingCostItem(true);
    try {
      let vendor_id = null;
      if (editForm.vendorValue) {
        const parts = editForm.vendorValue.split(":");
        vendor_id = parts.slice(1).join(":") || null;
      }
      await reportService.editCostItem({
        source_project_id: editingCostItem.project_id,
        kind: editingCostItem.type,
        index: editingCostItem.index,
        target_project_id: editForm.target_project_id || editingCostItem.project_id,
        item_name: editForm.item_name,
        description: editForm.description,
        cost: editForm.cost === "" ? 0 : parseFloat(editForm.cost),
        vendor_id: vendor_id,
        quantity: editForm.quantity === "" ? null : parseFloat(editForm.quantity),
        invoice_number: editForm.invoice_number,
        date: editForm.date || null,
      });
      toast({ description: "Line item updated successfully" });
      setEditingCostItem(null);
      setEditForm(null);
      queryClient.invalidateQueries({ queryKey: ['costs-and-wages'] });
    } catch (e) {
      toast({ variant: "destructive", description: e?.detail || e?.message || "Failed to update line item" });
    } finally {
      setSavingCostItem(false);
    }
  };

  // Build query parameters based on date range
  const getDateParams = () => {
    if (dateRange === "custom" && customDateFrom && customDateTo) {
      return { date_range: "custom", date_from: customDateFrom, date_to: customDateTo };
    }
    return { date_range: dateRange };
  };

  // Fetch real data from APIs with date filtering
  const { data: dashboardData } = useQuery({
    queryKey: ['reports-dashboard', dateRange, customDateFrom, customDateTo],
    queryFn: () => reportService.getDashboard(getDateParams())
  });

  const { data: projectsData } = useQuery({
    queryKey: ['reports-projects', dateRange, customDateFrom, customDateTo],
    queryFn: () => reportService.getProjects(getDateParams())
  });

  const { data: leadsData } = useQuery({
    queryKey: ['reports-leads', dateRange, customDateFrom, customDateTo],
    queryFn: () => reportService.getLeads(getDateParams())
  });

  const { data: customersData } = useQuery({
    queryKey: ['reports-customers', dateRange, customDateFrom, customDateTo],
    queryFn: () => reportService.getCustomers(getDateParams())
  });

  const { data: revenueData } = useQuery({
    queryKey: ['reports-revenue', dateRange, customDateFrom, customDateTo],
    queryFn: () => reportService.getRevenue(getDateParams())
  });

  const { data: periodData } = useQuery({
    queryKey: ['reports-period', dateRange],
    queryFn: () => reportService.getPeriodComparison(dateRange)
  });

  const { data: estimatedRevenueDetails } = useQuery({
    queryKey: ['reports-estimated-revenue-details', dateRange, customDateFrom, customDateTo],
    queryFn: () => reportService.getEstimatedRevenueDetails(getDateParams()),
    enabled: isEstimatedRevenueDialogOpen
  });

  // Queries for project metric dialogs
  const { data: projectsSoldData, isLoading: projectsSoldLoading } = useQuery({
    queryKey: ['reports-projects-sold', dateRange, customDateFrom, customDateTo],
    queryFn: () => reportService.getProjectsSold(getDateParams()),
    enabled: isProjectsSoldDialogOpen
  });

  const { data: projectsStartedData, isLoading: projectsStartedLoading } = useQuery({
    queryKey: ['reports-projects-started', dateRange, customDateFrom, customDateTo],
    queryFn: () => reportService.getProjectsStarted(getDateParams()),
    enabled: isProjectsStartedDialogOpen
  });

  const { data: projectsFinishedData, isLoading: projectsFinishedLoading } = useQuery({
    queryKey: ['reports-projects-finished', dateRange, customDateFrom, customDateTo],
    queryFn: () => reportService.getProjectsFinished(getDateParams()),
    enabled: isProjectsFinishedDialogOpen
  });

  // Costs & Wages queries
  const { data: subcontractorsData } = useQuery({
    queryKey: ['subcontractors'],
    queryFn: () => subcontractorService.getAll()
  });

  const { data: vendorsData } = useQuery({
    queryKey: ['vendors'],
    queryFn: () => vendorService.getAll()
  });

  const { data: designersData } = useQuery({
    queryKey: ['designers'],
    queryFn: () => designerService.getAll()
  });

  // Get all projects list for the project filter - only search when user types
  const { data: searchedProjectsData, isLoading: projectsSearchLoading } = useQuery({
    queryKey: ['projects-search', projectSearchQuery],
    queryFn: () => projectService.getAll({ search: projectSearchQuery, limit: 50 }),
    enabled: projectSearchQuery.length >= 2  // Only search when 2+ characters typed
  });

  // Full project list for the "move line item to project" dropdown
  const { data: allProjectsForMove } = useQuery({
    queryKey: ['all-projects-for-move'],
    queryFn: () => projectService.getAll({ limit: 1000 }),
  });

  const { data: costsAndWagesData, isLoading: costsLoading } = useQuery({
    queryKey: ['costs-and-wages', costsEntityType, costsEntityId, costsDateFrom, costsDateTo, costsSelectedProjects],
    queryFn: () => reportService.getCostsAndWages({
      entity_type: costsEntityType !== 'all' ? costsEntityType : undefined,
      entity_id: costsEntityId || undefined,
      date_from: costsDateFrom || undefined,
      date_to: costsDateTo || undefined,
      project_ids: costsSelectedProjects.length > 0 ? costsSelectedProjects.map(p => p.id).join(',') : undefined
    })
  });

  // Calculate metrics from real data
  const dashboard = dashboardData?.data || {};
  const projects = projectsData?.data || {};
  const leads = leadsData?.data || {};
  const customers = customersData?.data || {};
  const revenue = revenueData?.data || {};
  const period = periodData?.data || {};

  // Handle date range change
  const handleDateRangeChange = (value) => {
    if (value === "custom") {
      setIsCustomDateDialogOpen(true);
    } else {
      setDateRange(value);
    }
  };

  // Apply custom date range
  const handleApplyCustomDateRange = () => {
    if (customDateFrom && customDateTo) {
      setDateRange("custom");
      setIsCustomDateDialogOpen(false);
    }
  };

  // Get display label for date range
  const getDateRangeLabel = () => {
    if (dateRange === "custom" && customDateFrom && customDateTo) {
      return `${new Date(customDateFrom).toLocaleDateString()} - ${new Date(customDateTo).toLocaleDateString()}`;
    }
    const labels = {
      last_7_days: "Last 7 Days",
      last_30_days: "Last 30 Days",
      last_90_days: "Last 90 Days",
      year_to_date: "Year to Date",
      last_year: "Last Year"
    };
    return labels[dateRange] || "Last 30 Days";
  };

  const estimatedRevenue = dashboard.estimated_revenue || 0;
  const completedProjects = dashboard.completed_projects || 0;
  const totalProjects = dashboard.total_projects || 0;
  const avgProjectValue = completedProjects > 0 ? Math.round(estimatedRevenue / completedProjects) : (period.current_metrics?.avg_project_value || 0);
  const conversionRate = Math.round(leads.conversion_funnel?.conversion_rate || 0);

  // New project metrics
  const avgTicketSize = projects.avg_ticket_size || 0;
  const projectsSold = projects.projects_sold || 0;
  const projectsStarted = projects.projects_started || 0;
  const projectsFinished = projects.projects_finished || 0;

  // Process revenue data for charts (use project budget data if no revenue data)
  const monthlyRevenue = revenue.payments_received || [];
  const projectStatusData = projects.status_distribution || [];
  const leadSourcesData = leads.source_breakdown || [];
  const serviceBreakdown = revenue.service_breakdown || [];

  // Calculate total revenue received (sum of all payments in the period)
  const totalRevenueReceived = monthlyRevenue.reduce((sum, month) => sum + (month.payments_received || 0), 0);
  const totalPaymentCount = monthlyRevenue.reduce((sum, month) => sum + (month.payment_count || 0), 0);

  // Calculate period changes
  const formatChange = (change) => {
    if (change === null || change === undefined) return "+0.0%";
    const sign = change >= 0 ? "+" : "";
    return `${sign}${change.toFixed(1)}%`;
  };
  
  const avgProjectValueChange = formatChange(period.avg_project_value_change);
  const conversionRateChange = formatChange(period.conversion_rate_change);

  // Get current stats
  const totalLeads = dashboard.total_leads || 0;
  const activeProjects = dashboard.active_projects || 0;
  const totalCustomers = dashboard.total_customers || 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Reports & Analytics</h1>
          <p className="text-gray-600 mt-1">Track your business performance and insights</p>
        </div>
        <div className="flex space-x-3">
          <Select value={dateRange} onValueChange={handleDateRangeChange}>
            <SelectTrigger className="w-[240px]">
              <Calendar className="mr-2 h-4 w-4" />
              <SelectValue>{getDateRangeLabel()}</SelectValue>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="last_7_days">Last 7 Days</SelectItem>
              <SelectItem value="last_30_days">Last 30 Days</SelectItem>
              <SelectItem value="last_90_days">Last 90 Days</SelectItem>
              <SelectItem value="year_to_date">Year to Date</SelectItem>
              <SelectItem value="last_year">Last Year</SelectItem>
              <SelectItem value="custom">Custom Date Range...</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline">
            <Download className="mr-2 h-4 w-4" />
            Export
          </Button>
        </div>
      </div>

      {/* Custom Date Range Dialog */}
      <Dialog open={isCustomDateDialogOpen} onOpenChange={setIsCustomDateDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Custom Date Range</DialogTitle>
            <DialogDescription>
              Select a custom date range for your reports.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="dateFrom" className="text-right">
                From
              </Label>
              <Input
                id="dateFrom"
                type="date"
                value={customDateFrom}
                onChange={(e) => setCustomDateFrom(e.target.value)}
                className="col-span-3"
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="dateTo" className="text-right">
                To
              </Label>
              <Input
                id="dateTo"
                type="date"
                value={customDateTo}
                onChange={(e) => setCustomDateTo(e.target.value)}
                className="col-span-3"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCustomDateDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleApplyCustomDateRange}
              disabled={!customDateFrom || !customDateTo}
              className="bg-brand-600 hover:bg-brand-700"
            >
              Apply Date Range
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
        <Card
          className="border-green-200 bg-green-50 cursor-pointer hover:border-green-500 transition-colors"
          onClick={() => setIsRevenueReceivedDialogOpen(true)}
        >
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-green-800">Revenue Received</CardTitle>
            <DollarSign className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-700">${totalRevenueReceived.toLocaleString()}</div>
            <div className="flex items-center text-xs text-green-600 mt-1">
              {totalPaymentCount} payment{totalPaymentCount !== 1 ? 's' : ''} in selected period • Click to view
            </div>
          </CardContent>
        </Card>
        <Card
          className="cursor-pointer hover:border-brand-500 transition-colors"
          onClick={() => setIsEstimatedRevenueDialogOpen(true)}
        >
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Estimated Revenue</CardTitle>
            <DollarSign className="h-4 w-4 text-brand-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">${estimatedRevenue.toLocaleString()}</div>
            <div className="flex items-center text-xs text-muted-foreground mt-1">
              Click to view details • Excludes cancelled/on-hold
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Projects Completed</CardTitle>
            <BarChart3 className="h-4 w-4 text-blue-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{completedProjects}</div>
            <div className="flex items-center text-xs text-muted-foreground mt-1">
              Projects marked as &quot;Paid and Complete&quot;
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Avg Project Value</CardTitle>
            <DollarSign className="h-4 w-4 text-orange-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">${avgProjectValue.toLocaleString()}</div>
            <div className={`flex items-center text-xs mt-1 ${
              period.avg_project_value_change >= 0 ? 'text-green-600' : 'text-red-600'
            }`}>
              <TrendingUp className="mr-1 h-3 w-3" />
              {avgProjectValueChange} from last period
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Lead Conversion</CardTitle>
            <Users className="h-4 w-4 text-purple-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{conversionRate}%</div>
            <div className={`flex items-center text-xs mt-1 ${
              period.conversion_rate_change >= 0 ? 'text-green-600' : 'text-red-600'
            }`}>
              <TrendingUp className="mr-1 h-3 w-3" />
              {conversionRateChange} from last period
            </div>
          </CardContent>
        </Card>
        <Card className="border-amber-200 bg-amber-50">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-amber-800">Outstanding Balance</CardTitle>
            <DollarSign className="h-4 w-4 text-amber-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-amber-700">
              ${(dashboard.outstanding_balance || 0).toLocaleString()}
            </div>
            <div className="text-xs text-amber-600 mt-1">
              Current total • Not filtered by date
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Leads</CardTitle>
            <Users className="h-4 w-4 text-blue-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalLeads}</div>
            <div className="text-xs text-muted-foreground mt-1">
              In selected period
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Projects</CardTitle>
            <BarChart3 className="h-4 w-4 text-indigo-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{activeProjects}</div>
            <div className="text-xs text-muted-foreground mt-1">
              Current total • Not filtered by date
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Detailed Reports */}
      <Tabs defaultValue="revenue" className="space-y-6">
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="revenue">Revenue</TabsTrigger>
          <TabsTrigger value="projects">Projects</TabsTrigger>
          <TabsTrigger value="leads">Leads</TabsTrigger>
          <TabsTrigger value="customers">Customers</TabsTrigger>
          <TabsTrigger value="costs">Costs & Wages</TabsTrigger>
        </TabsList>

        <TabsContent value="revenue" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <BarChart3 className="mr-2 h-5 w-5 text-brand-600" />
                  Monthly Payments Received
                </CardTitle>
                <CardDescription>Payments received by month</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {monthlyRevenue.length > 0 ? (
                    monthlyRevenue.map((month, index) => (
                      <div key={index} className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <div className="w-12 text-sm font-medium">{month._id}</div>
                          <div className="flex-1">
                            <div className="h-6 bg-emerald-100 rounded-full overflow-hidden">
                              <div 
                                className="h-full bg-brand-600 rounded-full transition-all duration-300"
                                style={{ width: `${Math.min((month.payments_received / 50000) * 100, 100)}%` }}
                              ></div>
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="font-medium">${month.payments_received?.toLocaleString() || '0'}</div>
                          <div className="text-xs text-gray-500">{month.payment_count || 0} payments</div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      <p>No payment data available for this period</p>
                      <p className="text-sm">Payments with paid dates will appear here</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <PieChart className="mr-2 h-5 w-5 text-blue-600" />
                  Revenue Breakdown
                </CardTitle>
                <CardDescription>Revenue by service type</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {serviceBreakdown.length > 0 ? (
                    serviceBreakdown.map((service, index) => {
                      const colors = ['bg-emerald-500', 'bg-blue-500', 'bg-orange-500', 'bg-purple-500', 'bg-gray-500'];
                      const totalRevenue = serviceBreakdown.reduce((sum, s) => sum + s.total_revenue, 0);
                      const percentage = totalRevenue > 0 ? ((service.total_revenue / totalRevenue) * 100).toFixed(0) : 0;
                      
                      return (
                        <div key={index} className="flex items-center justify-between">
                          <div className="flex items-center space-x-2">
                            <div className={`w-3 h-3 ${colors[index % colors.length]} rounded-full`}></div>
                            <span className="text-sm">{service._id}</span>
                          </div>
                          <div className="text-right">
                            <div className="font-medium">${service.total_revenue.toLocaleString()}</div>
                            <div className="text-xs text-gray-500">{percentage}%</div>
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      <p>No revenue breakdown available yet</p>
                      <p className="text-sm">Complete some projects to see service analytics</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="projects" className="space-y-6">
          {/* New Metrics Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Avg. Project Ticket Size</CardTitle>
                <DollarSign className="h-4 w-4 text-green-600" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">${Math.round(avgTicketSize).toLocaleString()}</div>
                <p className="text-xs text-muted-foreground">
                  Based on Total Job Cost
                </p>
              </CardContent>
            </Card>
            <Card 
              className="cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => setIsProjectsSoldDialogOpen(true)}
            >
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Projects Sold</CardTitle>
                <FileText className="h-4 w-4 text-blue-600" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{projectsSold}</div>
                <p className="text-xs text-muted-foreground">
                  Click to view details
                </p>
              </CardContent>
            </Card>
            <Card 
              className="cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => setIsProjectsStartedDialogOpen(true)}
            >
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Projects Started</CardTitle>
                <PlayCircle className="h-4 w-4 text-orange-600" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{projectsStarted}</div>
                <p className="text-xs text-muted-foreground">
                  Click to view details
                </p>
              </CardContent>
            </Card>
            <Card 
              className="cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => setIsProjectsFinishedDialogOpen(true)}
            >
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Projects Finished</CardTitle>
                <CheckCircle className="h-4 w-4 text-green-600" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{projectsFinished}</div>
                <p className="text-xs text-muted-foreground">
                  Click to view details
                </p>
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Project Status Distribution</CardTitle>
                <CardDescription>Current status of all projects</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {projectStatusData.length > 0 ? (
                    projectStatusData.map((status, index) => (
                      <div key={index} className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <Badge variant="outline">{status._id || 'No Status'}</Badge>
                          <div className="flex-1">
                            <div className="h-4 bg-gray-100 rounded-full overflow-hidden">
                              <div 
                                className="h-full bg-blue-600 rounded-full transition-all duration-300"
                                style={{ width: `${Math.min((status.count / totalProjects) * 100, 100)}%` }}
                              ></div>
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="font-medium">{status.count}</div>
                          <div className="text-xs text-gray-500">{Math.round((status.count / totalProjects) * 100) || 0}%</div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      <p>No project data available yet</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Profitability</CardTitle>
                <CardDescription>Payments received minus all costs paid out in this period</CardDescription>
              </CardHeader>
              <CardContent>
                {projects.profitability && (projects.profitability.total_revenue > 0 || projects.profitability.total_costs > 0) ? (
                  <div className="space-y-5">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Total Revenue</span>
                      <span className="text-lg font-bold text-green-600">
                        ${projects.profitability.total_revenue?.toLocaleString()}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Total Costs</span>
                      <span className="text-lg font-bold text-red-600">
                        ${projects.profitability.total_costs?.toLocaleString()}
                      </span>
                    </div>
                    <div className="border-t pt-3 flex items-center justify-between">
                      <span className="text-sm font-medium">Gross Profit</span>
                      <span className={`text-lg font-bold ${projects.profitability.gross_profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        ${projects.profitability.gross_profit?.toLocaleString()}
                      </span>
                    </div>
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium">Profit Margin</span>
                        <span className={`text-sm font-bold ${projects.profitability.profit_margin >= 50 ? 'text-green-600' : projects.profitability.profit_margin >= 25 ? 'text-yellow-600' : 'text-red-600'}`}>
                          {projects.profitability.profit_margin}%
                        </span>
                      </div>
                      <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full transition-all duration-300 ${projects.profitability.profit_margin >= 50 ? 'bg-green-500' : projects.profitability.profit_margin >= 25 ? 'bg-yellow-500' : 'bg-red-500'}`}
                          style={{ width: `${Math.min(projects.profitability.profit_margin, 100)}%` }}
                        ></div>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3 pt-2 border-t text-xs text-gray-500">
                      <div>Materials: ${(projects.profitability.materials_cost || 0).toLocaleString()}</div>
                      <div>Receipts: ${(projects.profitability.receipts_cost || 0).toLocaleString()}</div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <p>No profitability data available</p>
                    <p className="text-sm">Add materials and receipts to projects to see profit metrics</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="leads" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Lead Sources</CardTitle>
                <CardDescription>Where your leads are coming from</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {leadSourcesData.length > 0 ? (
                    leadSourcesData.map((source, index) => (
                      <div key={index} className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <div className="w-20 text-sm font-medium">{source._id || 'Unknown'}</div>
                          <div className="flex-1">
                            <div className="h-4 bg-gray-100 rounded-full overflow-hidden">
                              <div 
                                className="h-full bg-orange-600 rounded-full transition-all duration-300"
                                style={{ width: `${totalLeads > 0 ? Math.min((source.count / totalLeads) * 100, 100) : 0}%` }}
                              ></div>
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="font-medium">{source.count}</div>
                          <div className="text-xs text-gray-500">{totalLeads > 0 ? Math.round((source.count / totalLeads) * 100) : 0}%</div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      <p>No lead source data available yet</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Lead Conversion Funnel</CardTitle>
                <CardDescription>How leads progress through your pipeline</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {/* Total Leads */}
                  <div className="text-center">
                    <div className="w-full h-12 bg-blue-100 rounded-t-lg flex items-center justify-center">
                      <span className="font-medium">Total Leads: {totalLeads}</span>
                    </div>
                  </div>
                  
                  {/* Dynamic stages based on actual lead statuses */}
                  {leads.conversion_funnel?.stages?.map((stage, index) => {
                    // Calculate width based on percentage (minimum 20% width for visibility)
                    const width = Math.max(20, stage.percentage);
                    const colors = [
                      'bg-indigo-100', 'bg-purple-100', 'bg-blue-100', 
                      'bg-cyan-100', 'bg-teal-100', 'bg-green-100', 
                      'bg-lime-100', 'bg-yellow-100', 'bg-orange-100'
                    ];
                    const color = colors[index % colors.length];
                    
                    return (
                      <div key={stage.status} className="text-center">
                        <div 
                          className={`h-10 ${color} rounded mx-auto flex items-center justify-center transition-all`}
                          style={{ width: `${width}%` }}
                        >
                          <span className="font-medium text-sm px-2">
                            {stage.label}: {stage.count} ({stage.percentage}%)
                          </span>
                        </div>
                      </div>
                    );
                  })}
                  
                  {/* Conversion to Customers - Bottom of funnel */}
                  <div className="text-center mt-6 pt-4 border-t">
                    <div className="text-xs text-gray-500 mb-2">CONVERSION TO CUSTOMERS</div>
                    <div className="w-1/4 h-12 bg-brand-600 text-white rounded-b-lg mx-auto flex items-center justify-center shadow-lg">
                      <span className="font-bold">
                        {leads.conversion_funnel?.converted_to_customers || 0} Converted ({leads.conversion_funnel?.conversion_rate || 0}%)
                      </span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="customers" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Customer Segments</CardTitle>
                <CardDescription>Customer breakdown by value</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {customers.value_segments && customers.value_segments.length > 0 ? (
                    customers.value_segments.map((segment, index) => {
                      const colors = ['bg-green-600', 'bg-blue-600', 'bg-orange-600', 'bg-gray-600'];
                      const totalCustomers = customers.value_segments.reduce((sum, s) => sum + s.count, 0);
                      const percentage = totalCustomers > 0 ? (segment.count / totalCustomers) * 100 : 0;
                      
                      return (
                        <div key={index} className="flex items-center justify-between">
                          <span className="text-sm">{segment._id}</span>
                          <div className="flex items-center">
                            <div className="w-20 h-2 bg-gray-100 rounded-full mr-2">
                              <div 
                                className={`h-2 ${colors[index % colors.length]} rounded-full`} 
                                style={{ width: `${Math.max(5, percentage)}%` }}
                              ></div>
                            </div>
                            <span className="text-sm font-medium">{segment.count}</span>
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      <p>No customer segments available yet</p>
                      <p className="text-sm">Add more customers and projects to see segmentation</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Customer Satisfaction</CardTitle>
                <CardDescription>Connect review platforms for feedback analytics</CardDescription>
              </CardHeader>
              <CardContent>
                {customers.satisfaction_metrics && customers.satisfaction_metrics.total_reviews > 0 ? (
                  <div className="space-y-4">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-yellow-600">
                        {customers.satisfaction_metrics.avg_rating.toFixed(1)}/5.0
                      </div>
                      <div className="text-sm text-gray-600">Average Rating</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-green-600">
                        {customers.satisfaction_metrics.recommendation_score}
                      </div>
                      <div className="text-sm text-gray-600">NPS Score</div>
                    </div>
                    <div className="text-center">
                      <div className="text-lg font-medium text-gray-600">
                        {customers.satisfaction_metrics.total_reviews} Reviews
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <div className="text-gray-400 mb-4">
                      <PieChart className="h-12 w-12 mx-auto" />
                    </div>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">Review Integration Required</h3>
                    <p className="text-sm text-gray-600 mb-4">
                      Connect your Google Business, Yelp, or other review platforms to see satisfaction metrics here.
                    </p>
                    <Button variant="outline" size="sm">
                      Setup Review Integration
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Repeat Customers</CardTitle>
                <CardDescription>Customer retention metrics</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-blue-600">
                      {Math.round(customers.repeat_customer_rate || 0)}%
                    </div>
                    <div className="text-sm text-gray-600">Repeat Rate</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-purple-600">
                      {(customers.lifetime_metrics?.avg_projects_per_customer || 0).toFixed(1)}
                    </div>
                    <div className="text-sm text-gray-600">Avg Projects/Customer</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-green-600">
                      ${Math.round(customers.lifetime_metrics?.avg_lifetime_value || 0).toLocaleString()}
                    </div>
                    <div className="text-sm text-gray-600">Customer Lifetime Value</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Costs & Wages Tab */}
        <TabsContent value="costs" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Receipt className="mr-2 h-5 w-5 text-brand-600" />
                Costs & Wages Report
              </CardTitle>
              <CardDescription>
                View materials and receipts filtered by subcontractor, vendor, or designer
              </CardDescription>
            </CardHeader>
            <CardContent>
              {/* Filters */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                <div className="space-y-2">
                  <Label>Entity Type</Label>
                  <Select value={costsEntityType} onValueChange={(value) => { setCostsEntityType(value); setCostsEntityId(""); }}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All</SelectItem>
                      <SelectItem value="subcontractor">Subcontractor</SelectItem>
                      <SelectItem value="vendor">Vendor</SelectItem>
                      <SelectItem value="designer">Designer</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>
                    {costsEntityType === 'subcontractor' ? 'Subcontractor' : 
                     costsEntityType === 'vendor' ? 'Vendor' : 
                     costsEntityType === 'designer' ? 'Designer' : 'Entity'}
                  </Label>
                  <Select 
                    value={costsEntityId || "all_entities"} 
                    onValueChange={(value) => setCostsEntityId(value === "all_entities" ? "" : value)}
                    disabled={costsEntityType === 'all'}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={costsEntityType === 'all' ? 'Select type first' : 'Select...'} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all_entities">All {costsEntityType !== 'all' ? costsEntityType + 's' : ''}</SelectItem>
                      {costsEntityType === 'subcontractor' && (subcontractorsData?.data || []).map(sub => (
                        <SelectItem key={sub.id || sub._id} value={sub.id || sub._id}>
                          {sub.name} {sub.specialty ? `(${sub.specialty})` : ''}
                        </SelectItem>
                      ))}
                      {costsEntityType === 'vendor' && (vendorsData?.data || []).map(vendor => (
                        <SelectItem key={vendor.id || vendor._id} value={vendor.id || vendor._id}>
                          {vendor.company_name || vendor.name}
                        </SelectItem>
                      ))}
                      {costsEntityType === 'designer' && (designersData?.data || []).map(designer => (
                        <SelectItem key={designer.id || designer._id} value={designer.id || designer._id}>
                          {designer.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>From Date</Label>
                  <Input
                    type="date"
                    value={costsDateFrom}
                    onChange={(e) => setCostsDateFrom(e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label>To Date</Label>
                  <Input
                    type="date"
                    value={costsDateTo}
                    onChange={(e) => setCostsDateTo(e.target.value)}
                  />
                </div>

                {/* Project Filter - Multi-select with Search */}
                <div className="space-y-2 col-span-2 md:col-span-1">
                  <Label>Filter by Projects</Label>
                  <div className="border rounded-md bg-white">
                    {/* Search Input */}
                    <div className="p-2 border-b">
                      <Input
                        type="text"
                        placeholder="Type to search projects..."
                        value={projectSearchQuery}
                        onChange={(e) => setProjectSearchQuery(e.target.value)}
                        className="h-8 text-sm"
                      />
                    </div>
                    
                    {/* Project List */}
                    <div className="p-2 max-h-48 overflow-y-auto">
                      {/* All Projects Option */}
                      <div className="flex items-center space-x-2 pb-2 border-b mb-2">
                        <input
                          type="checkbox"
                          id="all-projects"
                          checked={costsSelectedProjects.length === 0}
                          onChange={() => {
                            setCostsSelectedProjects([]);
                            setProjectSearchQuery("");
                          }}
                          className="h-4 w-4 rounded border-gray-300"
                        />
                        <label htmlFor="all-projects" className="text-sm font-medium text-gray-700">
                          All Projects
                        </label>
                      </div>
                      
                      {/* Selected Projects (always show) */}
                      {costsSelectedProjects.length > 0 && (
                        <div className="mb-2 pb-2 border-b">
                          <div className="text-xs text-gray-500 mb-1">Selected:</div>
                          {costsSelectedProjects.map(project => (
                            <div key={project.id} className="flex items-center space-x-2 py-1">
                              <input
                                type="checkbox"
                                checked={true}
                                onChange={() => {
                                  setCostsSelectedProjects(costsSelectedProjects.filter(p => p.id !== project.id));
                                }}
                                className="h-4 w-4 rounded border-gray-300"
                              />
                              <label className="text-sm text-gray-700">
                                {project.name}
                              </label>
                            </div>
                          ))}
                        </div>
                      )}
                      
                      {/* Search Results */}
                      {projectSearchQuery.length < 2 ? (
                        <div className="text-sm text-gray-400 py-2">Type at least 2 characters to search...</div>
                      ) : projectsSearchLoading ? (
                        <div className="text-sm text-gray-400 py-2">Searching...</div>
                      ) : (searchedProjectsData?.data || []).length === 0 ? (
                        <div className="text-sm text-gray-400 py-2">No projects found</div>
                      ) : (
                        (searchedProjectsData?.data || [])
                          .filter(project => !costsSelectedProjects.some(p => p.id === (project.id || project._id)))
                          .map(project => (
                            <div key={project.id || project._id} className="flex items-center space-x-2 py-1">
                              <input
                                type="checkbox"
                                id={`project-${project.id || project._id}`}
                                checked={false}
                                onChange={() => {
                                  setCostsSelectedProjects([...costsSelectedProjects, { id: project.id || project._id, name: project.name }]);
                                }}
                                className="h-4 w-4 rounded border-gray-300"
                              />
                              <label htmlFor={`project-${project.id || project._id}`} className="text-sm text-gray-700">
                                {project.name}
                              </label>
                            </div>
                          ))
                      )}
                    </div>
                  </div>
                  {costsSelectedProjects.length > 0 && (
                    <div className="text-xs text-gray-500">
                      {costsSelectedProjects.length} project(s) selected
                    </div>
                  )}
                </div>
              </div>

              {/* Summary */}
              <div className="grid grid-cols-2 gap-4 mb-6 p-4 bg-gray-50 rounded-lg">
                <div>
                  <div className="text-sm text-gray-600">Total Items</div>
                  <div className="text-2xl font-bold text-brand-600">
                    {costsAndWagesData?.data?.count || 0}
                  </div>
                </div>
                <div>
                  <div className="text-sm text-gray-600">Total Cost</div>
                  <div className="text-2xl font-bold text-red-600">
                    ${(costsAndWagesData?.data?.total_cost || 0).toLocaleString()}
                  </div>
                </div>
              </div>

              {/* Results Table */}
              {costsLoading ? (
                <div className="text-center py-8 text-gray-500">Loading...</div>
              ) : (costsAndWagesData?.data?.items || []).length > 0 ? (
                (() => {
                  // Sort items based on selected column / direction
                  const items = [...(costsAndWagesData?.data?.items || [])];
                  const dir = costsSortDir === "asc" ? 1 : -1;
                  const getVal = (it) => {
                    switch (costsSortBy) {
                      case "type": return (it.type || "").toLowerCase();
                      case "project": return (it.project_name || "").toLowerCase();
                      case "item": return (it.item_name || "").toLowerCase();
                      case "vendor": return (it.company_name || "").toLowerCase();
                      case "invoice": return (it.invoice_number || "").toString().toLowerCase();
                      case "date": return it.date || it.added_at || "";
                      case "cost": return parseFloat(it.cost || 0);
                      default: return 0;
                    }
                  };
                  items.sort((a, b) => {
                    const va = getVal(a), vb = getVal(b);
                    if (va < vb) return -1 * dir;
                    if (va > vb) return 1 * dir;
                    return 0;
                  });

                  const handleSort = (col) => {
                    if (costsSortBy === col) {
                      setCostsSortDir(costsSortDir === "asc" ? "desc" : "asc");
                    } else {
                      setCostsSortBy(col);
                      setCostsSortDir("asc");
                    }
                  };

                  const SortIndicator = ({ col }) => {
                    if (costsSortBy !== col) return <span className="text-gray-300 ml-1">↕</span>;
                    return <span className="text-brand-600 ml-1">{costsSortDir === "asc" ? "↑" : "↓"}</span>;
                  };

                  const headerClass = "px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer select-none hover:bg-gray-100";

                  return (
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className={headerClass} onClick={() => handleSort("type")}>Type<SortIndicator col="type" /></th>
                        <th className={headerClass} onClick={() => handleSort("project")}>Project<SortIndicator col="project" /></th>
                        <th className={headerClass} onClick={() => handleSort("item")}>Item<SortIndicator col="item" /></th>
                        <th className={headerClass} onClick={() => handleSort("vendor")}>Vendor/Sub/Designer<SortIndicator col="vendor" /></th>
                        <th className={headerClass} onClick={() => handleSort("invoice")}>Invoice #<SortIndicator col="invoice" /></th>
                        <th className={headerClass} onClick={() => handleSort("date")}>Date<SortIndicator col="date" /></th>
                        <th className={headerClass + " text-right"} onClick={() => handleSort("cost")}>Cost<SortIndicator col="cost" /></th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {items.map((item, index) => (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="px-4 py-3">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              item.type === 'material' ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'
                            }`}>
                              {item.type === 'material' ? 'Material' : 'Receipt'}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <button
                              onClick={() => handleProjectClick(item.project_id)}
                              className="text-brand-600 hover:text-brand-800 hover:underline text-sm"
                            >
                              {item.project_name}
                            </button>
                          </td>
                          <td className="px-4 py-3 text-sm">{item.item_name}</td>
                          <td className="px-4 py-3 text-sm">
                            <div>{item.company_name || '-'}</div>
                            {item.entity_type && (
                              <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs ${
                                item.entity_type === 'vendor' ? 'bg-green-100 text-green-700' :
                                item.entity_type === 'subcontractor' ? 'bg-orange-100 text-orange-700' :
                                'bg-indigo-100 text-indigo-700'
                              }`}>
                                {item.entity_type}
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600">
                            {item.invoice_number || '-'}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600">
                            {item.date ? formatDatePST(item.date) : '-'}
                          </td>
                          <td className="px-4 py-3 text-sm text-right font-medium text-red-600">
                            ${(item.cost || 0).toLocaleString()}
                          </td>
                          <td className="px-4 py-3 text-right">
                            <button
                              onClick={() => openEditCostItem(item)}
                              className="text-xs px-2 py-1 rounded border border-gray-300 text-gray-600 hover:bg-gray-100 hover:text-brand-600"
                              title="Edit this line item"
                            >
                              Edit
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                  );
                })()
              ) : (
                <div className="text-center py-8 text-gray-500">
                  No materials or receipts found matching the selected criteria.
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Estimated Revenue Details Dialog */}
      <Dialog open={isEstimatedRevenueDialogOpen} onOpenChange={setIsEstimatedRevenueDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Estimated Revenue Details</DialogTitle>
            <DialogDescription>
              Projects with start dates in the selected range ({getDateRangeLabel()})
            </DialogDescription>
          </DialogHeader>
          
          {estimatedRevenueDetails?.data ? (
            <div className="space-y-4">
              {/* Summary */}
              <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
                <div>
                  <div className="text-sm text-gray-600">Total Revenue</div>
                  <div className="text-2xl font-bold text-brand-600">
                    ${estimatedRevenueDetails.data.total_revenue?.toLocaleString() || '0'}
                  </div>
                </div>
                <div>
                  <div className="text-sm text-gray-600">Project Count</div>
                  <div className="text-2xl font-bold">
                    {estimatedRevenueDetails.data.project_count || 0}
                  </div>
                </div>
              </div>

              {/* Projects List */}
              <div className="space-y-2">
                <h4 className="font-semibold text-sm text-gray-700">Projects</h4>
                {estimatedRevenueDetails.data.projects?.length > 0 ? (
                  <div className="border rounded-lg divide-y">
                    {estimatedRevenueDetails.data.projects.map((project, index) => (
                      <div key={project._id || index} className="p-3 hover:bg-gray-50">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="font-medium">{project.name}</div>
                            {project.customer_name && (
                              <div className="text-sm text-gray-600">{project.customer_name}</div>
                            )}
                            <div className="flex gap-2 mt-1">
                              <Badge variant="outline" className="text-xs">
                                {project.status?.replace(/_/g, ' ')}
                              </Badge>
                              {project.start_date && (
                                <span className="text-xs text-gray-500">
                                  Start: {project.start_date}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="font-bold text-brand-600">
                              ${project.total_job_cost?.toLocaleString() || '0'}
                            </div>
                            {project.contract_price && project.contract_price !== project.total_job_cost && (
                              <div className="text-xs text-gray-500">
                                Contract: ${project.contract_price?.toLocaleString()}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    No projects found in this date range
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">Loading...</div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEstimatedRevenueDialogOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Revenue Received Dialog */}
      <Dialog open={isRevenueReceivedDialogOpen} onOpenChange={setIsRevenueReceivedDialogOpen}>
        <DialogContent className="sm:max-w-[800px] max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center text-green-700">
              <DollarSign className="mr-2 h-5 w-5" />
              Revenue Received - {getDateRangeLabel()}
            </DialogTitle>
            <DialogDescription>
              All payments received within the selected date range
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {/* Summary */}
            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <div className="flex justify-between items-center">
                <div>
                  <div className="text-sm text-green-600">Total Revenue Received</div>
                  <div className="text-2xl font-bold text-green-700">${totalRevenueReceived.toLocaleString()}</div>
                </div>
                <div className="text-right">
                  <div className="text-sm text-green-600">Total Payments</div>
                  <div className="text-2xl font-bold text-green-700">{totalPaymentCount}</div>
                </div>
              </div>
            </div>
            
            {/* Payment List */}
            {(revenue.payment_details || []).length > 0 ? (
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Project</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {(revenue.payment_details || []).map((payment, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900">{payment.paid_date}</td>
                        <td className="px-4 py-3 text-sm text-gray-900 font-medium">{payment.project_name}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{payment.customer_name}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{payment.payment_description}</td>
                        <td className="px-4 py-3 text-sm text-green-600 font-medium text-right">
                          ${payment.amount?.toLocaleString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                No payments received in this period
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsRevenueReceivedDialogOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Projects Sold Dialog */}
      <Dialog open={isProjectsSoldDialogOpen} onOpenChange={setIsProjectsSoldDialogOpen}>
        <DialogContent className="sm:max-w-[700px] max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Projects Sold - {getDateRangeLabel()}</DialogTitle>
            <DialogDescription>
              Projects with contract signed in the selected date range. Click a project name to view details.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {projectsSoldLoading ? (
              <div className="text-center py-4">Loading...</div>
            ) : projectsSoldData?.data?.length > 0 ? (
              <div className="space-y-3">
                {projectsSoldData.data.map((project) => (
                  <div 
                    key={project.id} 
                    className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50"
                  >
                    <div className="flex-1">
                      <div 
                        className="font-medium text-brand-600 hover:text-brand-800 cursor-pointer hover:underline"
                        onClick={() => handleProjectClick(project.id)}
                      >
                        {project.name}
                      </div>
                      <div className="text-sm text-gray-500 flex items-center gap-3">
                        <Badge variant="outline" className="text-xs">
                          {project.status?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        </Badge>
                        <span className="text-xs">
                          Signed: {project.contract_sign_date}
                        </span>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-semibold text-green-600">
                        ${(project.total_job_cost || 0).toLocaleString()}
                      </div>
                    </div>
                  </div>
                ))}
                <div className="pt-4 border-t">
                  <div className="flex justify-between text-sm font-medium">
                    <span>Total Projects Sold:</span>
                    <span>{projectsSoldData.data.length}</span>
                  </div>
                  <div className="flex justify-between text-sm font-medium mt-1">
                    <span>Total Value:</span>
                    <span className="text-green-600">
                      ${projectsSoldData.data.reduce((sum, p) => sum + (p.total_job_cost || 0), 0).toLocaleString()}
                    </span>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-4 text-gray-500">No projects sold in this date range</div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Projects Started Dialog */}
      <Dialog open={isProjectsStartedDialogOpen} onOpenChange={setIsProjectsStartedDialogOpen}>
        <DialogContent className="sm:max-w-[700px] max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Projects Started - {getDateRangeLabel()}</DialogTitle>
            <DialogDescription>
              Projects with start date in the selected date range. Click a project name to view details.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {projectsStartedLoading ? (
              <div className="text-center py-4">Loading...</div>
            ) : projectsStartedData?.data?.length > 0 ? (
              <div className="space-y-3">
                {projectsStartedData.data.map((project) => (
                  <div 
                    key={project.id} 
                    className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50"
                  >
                    <div className="flex-1">
                      <div 
                        className="font-medium text-brand-600 hover:text-brand-800 cursor-pointer hover:underline"
                        onClick={() => handleProjectClick(project.id)}
                      >
                        {project.name}
                      </div>
                      <div className="text-sm text-gray-500 flex items-center gap-3">
                        <Badge variant="outline" className="text-xs">
                          {project.status?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        </Badge>
                        <span className="text-xs">
                          Started: {project.start_date}
                        </span>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-semibold text-green-600">
                        ${(project.total_job_cost || 0).toLocaleString()}
                      </div>
                    </div>
                  </div>
                ))}
                <div className="pt-4 border-t">
                  <div className="flex justify-between text-sm font-medium">
                    <span>Total Projects Started:</span>
                    <span>{projectsStartedData.data.length}</span>
                  </div>
                  <div className="flex justify-between text-sm font-medium mt-1">
                    <span>Total Value:</span>
                    <span className="text-green-600">
                      ${projectsStartedData.data.reduce((sum, p) => sum + (p.total_job_cost || 0), 0).toLocaleString()}
                    </span>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-4 text-gray-500">No projects started in this date range</div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Projects Finished Dialog */}
      <Dialog open={isProjectsFinishedDialogOpen} onOpenChange={setIsProjectsFinishedDialogOpen}>
        <DialogContent className="sm:max-w-[700px] max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Projects Finished - {getDateRangeLabel()}</DialogTitle>
            <DialogDescription>
              Projects completed (Paid & Complete) in the selected date range. Click a project name to view details.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {projectsFinishedLoading ? (
              <div className="text-center py-4">Loading...</div>
            ) : projectsFinishedData?.data?.length > 0 ? (
              <div className="space-y-3">
                {projectsFinishedData.data.map((project) => (
                  <div 
                    key={project.id} 
                    className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50"
                  >
                    <div className="flex-1">
                      <div 
                        className="font-medium text-brand-600 hover:text-brand-800 cursor-pointer hover:underline"
                        onClick={() => handleProjectClick(project.id)}
                      >
                        {project.name}
                      </div>
                      <div className="text-sm text-gray-500 flex items-center gap-3">
                        <Badge variant="outline" className="text-xs bg-green-100 text-green-800">
                          Paid & Complete
                        </Badge>
                        <span className="text-xs">
                          Completed: {new Date(project.finish_date || project.status_changed_at || project.updated_at).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-semibold text-green-600">
                        ${(project.total_job_cost || 0).toLocaleString()}
                      </div>
                    </div>
                  </div>
                ))}
                <div className="pt-4 border-t">
                  <div className="flex justify-between text-sm font-medium">
                    <span>Total Projects Finished:</span>
                    <span>{projectsFinishedData.data.length}</span>
                  </div>
                  <div className="flex justify-between text-sm font-medium mt-1">
                    <span>Total Value:</span>
                    <span className="text-green-600">
                      ${projectsFinishedData.data.reduce((sum, p) => sum + (p.total_job_cost || 0), 0).toLocaleString()}
                    </span>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-4 text-gray-500">No projects finished in this date range</div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Project Detail Dialog (for viewing project details within Reports page) */}
      {selectedProjectId && (
        <ProjectDetailDialog
          selectedProject={selectedProjectData}
          isDetailDialogOpen={isProjectDetailOpen}
          setIsDetailDialogOpen={(open) => {
            setIsProjectDetailOpen(open);
            if (!open) {
              setSelectedProjectId(null);
            }
          }}
          loadingProjectDetails={loadingProjectDetails}
          designers={[]}
          subcontractors={[]}
          onProjectUpdated={() => {
            queryClient.invalidateQueries({ queryKey: ['project-detail', selectedProjectId] });
            // Refresh the reports data too
            queryClient.invalidateQueries({ queryKey: ['reports-projects-finished'] });
          }}
        />
      )}

      {/* Edit Cost Line Item Dialog */}
      <Dialog open={!!editingCostItem} onOpenChange={(open) => { if (!open) { setEditingCostItem(null); setEditForm(null); } }}>
        <DialogContent className="sm:max-w-[560px] max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit {editingCostItem?.type === 'material' ? 'Material' : 'Receipt'}</DialogTitle>
            <DialogDescription>Edit any field, or move this line item to a different project.</DialogDescription>
          </DialogHeader>
          {editForm && (
            <div className="space-y-3 py-2">
              <div>
                <Label className="text-xs">Project</Label>
                <Select value={editForm.target_project_id || ""} onValueChange={(v) => setEditForm({ ...editForm, target_project_id: v })}>
                  <SelectTrigger><SelectValue placeholder="Select project" /></SelectTrigger>
                  <SelectContent className="max-h-72">
                    {(allProjectsForMove?.data || [])
                      .slice()
                      .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                      .map((p) => (
                        <SelectItem key={p.id || p._id} value={p.id || p._id}>{p.name}</SelectItem>
                      ))}
                  </SelectContent>
                </Select>
                {editForm.target_project_id !== editingCostItem?.project_id && (
                  <p className="text-xs text-amber-600 mt-1">This item will be MOVED to the selected project.</p>
                )}
              </div>

              <div>
                <Label className="text-xs">Item / Name</Label>
                <Input value={editForm.item_name} onChange={(e) => setEditForm({ ...editForm, item_name: e.target.value })} />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Cost ($)</Label>
                  <Input type="number" step="0.01" value={editForm.cost} onChange={(e) => setEditForm({ ...editForm, cost: e.target.value })} />
                </div>
                <div>
                  <Label className="text-xs">Quantity</Label>
                  <Input value={editForm.quantity} onChange={(e) => setEditForm({ ...editForm, quantity: e.target.value })} />
                </div>
              </div>

              <div>
                <Label className="text-xs">Vendor / Subcontractor / Designer</Label>
                <Select value={editForm.vendorValue || "__none__"} onValueChange={(v) => setEditForm({ ...editForm, vendorValue: v === "__none__" ? "" : v })}>
                  <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent className="max-h-72">
                    <SelectItem value="__none__">— None —</SelectItem>
                    {(vendorsData?.data || []).map((v) => (
                      <SelectItem key={`v-${v.id || v._id}`} value={`vendor:${v.id || v._id}`}>{v.company_name || v.name} (vendor)</SelectItem>
                    ))}
                    {(subcontractorsData?.data || []).map((s) => (
                      <SelectItem key={`s-${s.id || s._id}`} value={`subcontractor:${s.id || s._id}`}>{s.name} (sub)</SelectItem>
                    ))}
                    {(designersData?.data || []).map((d) => (
                      <SelectItem key={`d-${d.id || d._id}`} value={`designer:${d.id || d._id}`}>{d.name} (designer)</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Invoice #</Label>
                  <Input value={editForm.invoice_number} onChange={(e) => setEditForm({ ...editForm, invoice_number: e.target.value })} />
                </div>
                <div>
                  <Label className="text-xs">Date</Label>
                  <Input type="date" value={editForm.date} onChange={(e) => setEditForm({ ...editForm, date: e.target.value })} />
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setEditingCostItem(null); setEditForm(null); }}>Cancel</Button>
            <Button onClick={handleSaveCostItem} disabled={savingCostItem} className="bg-brand-600 hover:bg-brand-700">
              {savingCostItem ? "Saving..." : "Save Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}